import { AIServiceEnum, ServiceConfigs } from '../dataRegistry';

/**
 * Parses a version string into an array of numbers for easier comparison.
 * @param {string} v - The version string to parse.
 * @returns {number[]} An array of numbers representing the version.
 */
const parseVersion = (v: string) => v.split('.').map(Number);

/**
 * Compares a version string with a given range.
 * @param {string} version - The version string to compare.
 * @param {string} start - The start of the version range.
 * @param {string} [end] - The end of the version range.
 * @returns {boolean} True if the version is within the range, false otherwise.
 */
const isVersionInRange = (
  version: string,
  start: string,
  end?: string
): boolean => {
  const ver = parseVersion(version);
  const rangeStart = parseVersion(start);
  const rangeEnd = end ? parseVersion(end) : rangeStart;

  for (
    let i = 0;
    i < Math.max(ver.length, rangeStart.length, rangeEnd.length);
    i++
  ) {
    const verPart = ver[i] || 0;
    const startPart = rangeStart[i] || 0;
    const endPart = rangeEnd[i] || 0;
    if (verPart < startPart || verPart > endPart) return false;
  }
  return true;
};

/**
 * Determines the configuration for a given service and version.
 * If an exact match is not found, returns the configuration for the closest previous range.
 * @param {AIServiceEnum} service - The AIServiceEnum value for which to retrieve the configuration.
 * @param {string} version - The version for which to determine the configuration.
 * @returns {object | null} The configuration object for the given service and version.
 */
export const getConfigForServiceAndVersion = (
  service: AIServiceEnum,
  version: string
): object | null => {
  const serviceConfig = ServiceConfigs[service];
  if (!serviceConfig) {
    return null;
  }

  let closestConfig = null;
  for (const range in serviceConfig) {
    const [start, end] = range.split('-');
    if (isVersionInRange(version, start, end)) {
      return serviceConfig[range];
    } else if (parseVersion(version)[0] >= parseVersion(start)[0]) {
      closestConfig = serviceConfig[range];
    }
  }

  return closestConfig; // Returns the closest previous range config if exact match is not found
};

/**
 * Checks if a given value is a member of the AIServiceEnum.
 *
 * This function iterates through the values of the AIServiceEnum and checks
 * if the provided value matches any of the enum values. It's useful for
 * type-guarding a value to ensure it aligns with the expected enum.
 *
 * @param {any} value - The value to check against the AIServiceEnum.
 * @returns {boolean} - Returns true if the value is a member of AIServiceEnum, false otherwise.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAIServiceEnum(value: any): value is AIServiceEnum {
  return Object.values(AIServiceEnum).includes(value);
}
